<template>
  <div class="final-gradient-parameters">
    <b-row
      v-if="isLoading"
      class="final-gradient-parameters__loader"
      align-h="center"
      align-v="center"
    >
      <s-loader size="lg" />
    </b-row>
    <div v-if="!isLoading && parameters">
      <div class="final-gradient-parameters-general mb-3">
        <div class="final-gradient-parameters-general__title">
          {{ $t('engineering.final-gradient.parameters.minimum-allowable-time') }}
        </div>
        <div class="p-3">
          <b-row>
            <b-col>
              <div class="final-gradient-parameters-general__input form-group">
                <label>
                  {{ getLabel('alarmTemperatureReachTime') }}<span v-if="editMode">*</span>
                </label>
                <s-input-label-edit
                  v-model="$v.parameters.alarmTemperatureReachTime.$model"
                  :formatted-value="getFormattedNumber(
                      $v.parameters.alarmTemperatureReachTime.$model,
                      'alarmTemperatureReachTime'
                    )"
                  :editable="editMode"
                >
                  <b-form-spinbutton
                    v-model="$v.parameters.alarmTemperatureReachTime.$model"
                    v-bind="getInputOptions('alarmTemperatureReachTime')"
                  />
                  <b-form-invalid-feedback
                    v-if="getErrorState('alarmTemperatureReachTime')"
                    id="input-live-feedback-alarm-temperature-reach-time"
                  >
                    <div v-html="getRangeValidationFeedback('alarmTemperatureReachTime')">
                    </div>
                    <div v-html="getMultipleValidationFeedback('alarmTemperatureReachTime')">
                    </div>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <div class="final-gradient-parameters-general__input form-group">
                <label>
                  {{ getLabel('shutdownTemperatureReachTime') }}<span v-if="editMode">*</span>
                </label>
                <s-input-label-edit
                  v-model="$v.parameters.shutdownTemperatureReachTime.$model"
                  :formatted-value="getFormattedNumber(
                      $v.parameters.shutdownTemperatureReachTime.$model,
                      'shutdownTemperatureReachTime'
                    )"
                  :editable="editMode"
                >
                  <b-form-spinbutton
                    v-model="$v.parameters.shutdownTemperatureReachTime.$model"
                    v-bind="getInputOptions('shutdownTemperatureReachTime')"
                  />
                  <b-form-invalid-feedback
                    v-if="getErrorState('shutdownTemperatureReachTime')"
                    id="input-live-feedback-shutdown-temperature-reach-Time"
                  >
                    <div v-html="getRangeValidationFeedback('shutdownTemperatureReachTime')">
                    </div>
                    <div v-html="getMultipleValidationFeedback('shutdownTemperatureReachTime')">
                    </div>
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import parametersFields
  from '@/domain/data/installation/engineering/finalGradient/parametersFields';
import validations from '@/domain/data/installation/engineering/finalGradient/validations';

export default {
  name: 'FinalGradientParametersTab',
  props: {
    parameters: {
      type: Object,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  validations() {
    return validations(this.parameters);
  },
  computed: {
    hasUserPermission() {
      return this.$hasUserPermission('PER013');
    },
    isDisabled() {
      return !this.editMode || this.isSaving;
    },
    isInvalid() {
      return this.$v.$invalid;
    },
    parametersFields() {
      return parametersFields(this.parameters);
    },
  },
  methods: {
    getFormattedNumber(value, fieldName) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: this.parametersFields[fieldName].precision,
        maximumFractionDigits: this.parametersFields[fieldName].precision,
      });
    },
    getLabel(fieldName) {
      return this.$t(this.parametersFields[fieldName].label);
    },
    getInputOptions(fieldName) {
      const state = this.getErrorBsState(fieldName);

      return {
        type: 'number',
        step: this.parameters.algorithmExecutionCycleTime,
        state,
        required: this.parametersFields[fieldName].required,
        min: this.parametersFields[fieldName].minValue,
        max: this.parametersFields[fieldName].maxValue,
        maxlength: this.parametersFields[fieldName].maxLength,
        number: true,
        disabled: this.isDisabled,
      };
    },
    getErrorState(fieldName) {
      return this.$v.parameters[fieldName].$invalid;
    },
    getErrorBsState(fieldName) {
      return this.$bsStateValidator(this.$v.parameters[fieldName].$invalid);
    },
    getRangeValidationFeedback(fieldName) {
      if (!this.$v.parameters[fieldName].required
        || !this.$v.parameters[fieldName].numeric
        || !this.$v.parameters[fieldName].between) {
        return this.$t('messages.MA086.message',
          {
            minValue: this.getFormattedNumber(this.parametersFields[fieldName].minValue, fieldName),
            maxValue: this.getFormattedNumber(this.parametersFields[fieldName].maxValue, fieldName),
          });
      }
      return '';
    },
    getMultipleValidationFeedback(fieldName) {
      if (!this.$v.parameters[fieldName].multiple) {
        return this.$t('messages.MA089.message',
          {
            value: this.parameters.algorithmExecutionCycleTime,
          });
      }
      return '';
    },
  },
  watch: {
    isInvalid: {
      handler(newValue) {
        this.$emit('invalid-form', newValue);
      },
      immediate: true,
    },
  },
};
</script>

<style lang="sass" scoped>

@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.final-gradient-parameters
  &__loader
    height: 100px

  &-general
    padding: 5px
    border: 1px solid $color-border-gray
    border-radius: 3px

    &__title
      padding: .35rem 1rem
      border-radius: 5px
      font-weight: bold
      background-color: var(--color-background-dark)

    &__input
      & .label-edit_editing > input
        text-align: right

      & .label-edit_viewing
        opacity: .65

+media-min-md()
  .final-gradient-parameters
    &-general
      &__input
        & .label-edit_editing > .b-form-spinbutton
          max-width: 9em

+media-max-sm()
  .final-gradient-parameters
    &-general
      &__input
        & .label-edit_editing > .b-form-spinbutton
          max-width: 100%

@media print
  .final-gradient-parameters
    &-general
      &__input
        & .label-edit_editing > .b-form-spinbutton
          max-width: 9em

</style>
